import { useState, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Stepper, useSteps } from "@/components/ui/stepper";
import { trpc } from "@/trpc";
import { toast } from "sonner";
import SincoCredentialsForm, {
  type SincoCredentials,
  type ExistingConnection,
} from "./SincoCredentialsForm";
import EmpresaSelector, { type SincoCompany } from "./EmpresaSelector";
import MacroproyectoProyectoSelector, {
  type MasterProject,
  type Project,
} from "./MacroproyectoProyectoSelector";
import RecaudoSummary from "./RecaudoSummary";
import RecaudoResults, { type OverdueSale } from "./RecaudoResults";
import RecaudoConfirmation from "./RecaudoConfirmation";
import RecaudoChannelSelector from "./RecaudoChannelSelector";
import RecaudoVoiceConfirmation from "./RecaudoVoiceConfirmation";
import Step3SelectTemplate from "../broadcast/steps/Step3SelectTemplate";

const STEPS = [
  { title: "Credenciales" },
  { title: "Empresa" },
  { title: "Proyecto" },
  { title: "Canal" },
  { title: "Plantilla" },
  { title: "Resumen" },
  { title: "Resultados" },
  { title: "Confirmar" },
];

const DEFAULT_EXCLUDED_CATEGORIES = [
  "CREDITOS",
  "CANJES",
  "LOTES",
  "MAYOR VALOR",
  "OFICINA",
  "PERMUTAS",
  "SUBSIDIOS",
];

type Props = {
  merchantId: string;
};

const RecaudoWizard = ({ merchantId }: Props) => {
  const { activeStep, setActiveStep, goToNext, goToPrevious } = useSteps({ count: STEPS.length });

  const handleStepClick = useCallback(
    (index: number) => {
      if (index < activeStep) setActiveStep(index);
    },
    [activeStep, setActiveStep]
  );

  const [credentials, setCredentials] = useState<SincoCredentials | null>(null);
  const [generalToken, setGeneralToken] = useState<string | null>(null);
  const [companies, setCompanies] = useState<SincoCompany[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<SincoCompany | null>(null);
  const [companyToken, setCompanyToken] = useState<string | null>(null);
  const [masterProjects, setMasterProjects] = useState<MasterProject[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedMasterProjectId, setSelectedMasterProjectId] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [previewResult, setPreviewResult] = useState<{
    overdueSales: OverdueSale[];
    totalContacts: number;
  } | null>(null);
  const [continuedFromStored, setContinuedFromStored] = useState(false);
  const [excludedCategories, setExcludedCategories] = useState<string[]>(
    DEFAULT_EXCLUDED_CATEGORIES
  );

  // Channel state
  const [selectedChannel, setSelectedChannel] = useState<"whatsapp" | "voice">("whatsapp");

  // New state for WhatsApp flow
  const [selectedSaleIds, setSelectedSaleIds] = useState<string[]>([]);
  const [selectedWhatsappConnectionId, setSelectedWhatsappConnectionId] = useState<string>("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [broadcastName, setBroadcastName] = useState<string>("");

  const utils = trpc.useUtils();

  // Fetch connections to resolve wabaId for the template step
  const { data: whatsappConnections, isLoading: isLoadingConnections } =
    trpc.whatsappConnections.list.useQuery(
      { merchantId: merchantId || "" },
      { enabled: !!merchantId }
    );
  const selectedConnection = whatsappConnections?.find(
    (c) => c.id === selectedWhatsappConnectionId
  );
  const wabaId = selectedConnection?.wabaId;

  const storedCredentialsQuery = trpc.collections.getTokenFromStoredCredentials.useQuery(
    { merchantId },
    { enabled: activeStep === 0 && !!merchantId }
  );

  const voiceCallConfigQuery = trpc.collections.getVoiceCallConfig.useQuery(
    { merchantId },
    { enabled: activeStep === 3 && !!merchantId }
  );

  const activeVoiceCountQuery = trpc.collections.getActiveVoiceCount.useQuery(
    { merchantId },
    {
      enabled: selectedChannel === "voice" && activeStep >= 3 && !!merchantId,
      refetchInterval: 10_000,
    }
  );

  const authGeneralMutation = trpc.collections.authGeneral.useMutation({
    onSuccess: (data) => {
      setGeneralToken(data.accessToken);
      goToNext();
    },
    onError: (err) => {
      toast.error(err.message ?? "Error al conectar");
    },
  });

  const listCompaniesQuery = trpc.collections.listEmpresas.useQuery(
    {
      merchantId,
      baseUrl: credentials?.baseUrl ?? "",
      accessToken: generalToken ?? "",
    },
    { enabled: !!credentials && !!generalToken && activeStep === 1 }
  );

  const updateSincoEmpresaMutation = trpc.collections.updateSincoEmpresa.useMutation();

  const authEmpresaMutation = trpc.collections.authEmpresa.useMutation({
    onSuccess: async (data) => {
      setCompanyToken(data.accessToken);
      if (!selectedCompany) return;
      if (continuedFromStored) {
        await updateSincoEmpresaMutation.mutateAsync({
          merchantId,
          apiKey: data.accessToken,
          idOrigen: selectedCompany.IdOrigen,
          idEmpresa: selectedCompany.IdEmpresa,
          empresaNombre: selectedCompany.Nombre,
        });
      } else if (credentials) {
        await saveSincoMutation.mutateAsync({
          merchantId,
          sinco: {
            apiKey: data.accessToken,
            apiUrl: `${credentials.baseUrl.replace(/\/$/, "")}/V3/CBRClientes/API`,
            authUrl: credentials.authUrl,
            baseUrl: credentials.baseUrl,
            nomUsuario: credentials.nomUsuario,
            claveUsuario: credentials.claveUsuario,
            idOrigen: selectedCompany.IdOrigen,
            idEmpresa: selectedCompany.IdEmpresa,
            empresaNombre: selectedCompany.Nombre,
          },
        });
      }
      setActiveStep(2);
      utils.merchants.get.invalidate({ id: merchantId });
    },
    onError: (err) => {
      toast.error(err.message ?? "Error al autenticar empresa");
    },
  });

  const saveSincoMutation = trpc.collections.saveSincoConfig.useMutation();

  const listMasterProjectsQuery = trpc.collections.listMacroproyectos.useQuery(
    { merchantId },
    { enabled: !!merchantId && activeStep === 2 && !!companyToken }
  );

  const listProjectsQuery = trpc.collections.listProyectos.useQuery(
    { merchantId, idMacro: selectedMasterProjectId! },
    { enabled: !!merchantId && !!selectedMasterProjectId && activeStep === 2 }
  );

  const getExcludedCategoriesQuery = trpc.collections.getExcludedCategories.useQuery(
    { merchantId },
    { enabled: activeStep === 5 && !!merchantId }
  );

  const updateExcludedCategoriesMutation = trpc.collections.updateExcludedCategories.useMutation();

  const previewOverdueSalesQuery = trpc.collections.previewVentasEnMora.useQuery(
    {
      merchantId,
      idProyecto: selectedProjectId!,
      excludedConceptCategories: excludedCategories,
    },
    { enabled: false }
  );

  const handleCredentialsSubmit = useCallback(
    (creds: SincoCredentials) => {
      setCredentials(creds);
      setContinuedFromStored(false);
      authGeneralMutation.mutate({
        merchantId,
        authUrl: creds.authUrl,
        baseUrl: creds.baseUrl,
        nomUsuario: creds.nomUsuario,
        claveUsuario: creds.claveUsuario,
      });
    },
    [merchantId, authGeneralMutation]
  );

  const handleContinueWithStored = useCallback(
    (data: ExistingConnection) => {
      setCredentials({
        baseUrl: data.baseUrl,
        authUrl: data.authUrl,
        nomUsuario: data.nomUsuario,
        claveUsuario: "",
      });
      setGeneralToken(data.accessToken);
      setContinuedFromStored(true);
      goToNext();
    },
    [goToNext]
  );

  const handleEmpresaConfirm = useCallback(() => {
    if (!selectedCompany || !generalToken) return;
    if (!continuedFromStored && !credentials) return;
    authEmpresaMutation.mutate({
      merchantId,
      baseUrl: credentials?.baseUrl ?? "",
      generalToken,
      idOrigen: selectedCompany.IdOrigen,
      idEmpresa: selectedCompany.IdEmpresa,
    });
  }, [
    merchantId,
    selectedCompany,
    generalToken,
    credentials,
    continuedFromStored,
    authEmpresaMutation,
  ]);

  useEffect(() => {
    if (listCompaniesQuery.data) {
      setCompanies(listCompaniesQuery.data);
    }
  }, [listCompaniesQuery.data]);

  useEffect(() => {
    if (listMasterProjectsQuery.data) {
      const arr = Array.isArray(listMasterProjectsQuery.data) ? listMasterProjectsQuery.data : [];
      setMasterProjects(arr);
    }
  }, [listMasterProjectsQuery.data]);

  useEffect(() => {
    if (listProjectsQuery.data) {
      const arr = Array.isArray(listProjectsQuery.data) ? listProjectsQuery.data : [];
      setProjects(arr);
    }
  }, [listProjectsQuery.data]);

  useEffect(() => {
    if (getExcludedCategoriesQuery.data) {
      setExcludedCategories(getExcludedCategoriesQuery.data);
    }
  }, [getExcludedCategoriesQuery.data]);

  useEffect(() => {
    if (previewOverdueSalesQuery.data) {
      setPreviewResult(previewOverdueSalesQuery.data);
      // Clear selections when new preview loads
      setSelectedSaleIds([]);
      setActiveStep(6);
    }
  }, [previewOverdueSalesQuery.data, setActiveStep]);

  const getMasterProjectId = (m: MasterProject) => String(m.id ?? m.Id ?? "");
  const getMasterProjectName = (m: MasterProject): string =>
    String(m.nombre ?? (m as { Nombre?: string }).Nombre ?? "Sin nombre");
  // SINCO appends "- CBR" (internal branch code) to project names — strip it so customers see clean names
  const getProjectName = (p: Project): string =>
    String(p.nombre ?? (p as { Nombre?: string }).Nombre ?? "Sin nombre").replace(/\s*-\s*CBR$/i, '');

  const selectedMasterProject = masterProjects.find((m) => getMasterProjectId(m) === selectedMasterProjectId);
  const selectedProject = projects.find((p) => String(p.id ?? p.Id ?? "") === selectedProjectId);

  const handleToggleCategory = useCallback((category: string) => {
    setExcludedCategories((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
    );
  }, []);

  const handleLoadOverdueSales = useCallback(async () => {
    if (!selectedProjectId) return;
    try {
      await updateExcludedCategoriesMutation.mutateAsync({
        merchantId,
        excludedConceptCategories: excludedCategories,
      });
    } catch (err) {
      toast.error((err as Error).message ?? "Error al guardar categorías excluidas");
    }
    previewOverdueSalesQuery.refetch();
  }, [
    selectedProjectId,
    merchantId,
    excludedCategories,
    updateExcludedCategoriesMutation,
    previewOverdueSalesQuery,
  ]);

  const handleClose = useCallback(() => {
    setActiveStep(0);
    setSelectedChannel("whatsapp");
    setSelectedSaleIds([]);
    setSelectedWhatsappConnectionId("");
    setSelectedTemplateId("");
    setBroadcastName("");
  }, [setActiveStep]);

  const isNextDisabled = () => {
    if (activeStep === 3) {
      if (selectedChannel === "voice") return !voiceCallConfigQuery.data;
      return !selectedWhatsappConnectionId;
    }
    if (activeStep === 4) return !selectedTemplateId;
    return false;
  };

  const handleNextFromChannel = useCallback(() => {
    if (selectedChannel === "voice") {
      // Skip template step for voice
      setActiveStep(5);
    } else {
      goToNext();
    }
  }, [selectedChannel, setActiveStep, goToNext]);

  const handleBack = useCallback(() => {
    if (selectedChannel === "voice" && activeStep === 5) {
      setActiveStep(3);
    } else {
      goToPrevious();
    }
  }, [selectedChannel, activeStep, setActiveStep, goToPrevious]);

  return (
    <div className="space-y-6">
      <Stepper activeStep={activeStep} steps={STEPS} onStepClick={handleStepClick} />
      <div className="min-h-[300px]">
        {activeStep === 0 && (
          <SincoCredentialsForm
            initialValues={credentials ?? undefined}
            existingConnection={storedCredentialsQuery.data ?? null}
            onSubmit={handleCredentialsSubmit}
            onContinueWithStored={handleContinueWithStored}
            isLoading={authGeneralMutation.isLoading}
            isLoadingStoredCheck={storedCredentialsQuery.isLoading}
          />
        )}
        {activeStep === 1 && (
          <EmpresaSelector
            companies={companies}
            selectedCompany={selectedCompany}
            onSelect={setSelectedCompany}
            onConfirm={handleEmpresaConfirm}
            isLoading={authEmpresaMutation.isLoading}
          />
        )}
        {activeStep === 2 && (
          <MacroproyectoProyectoSelector
            masterProjects={masterProjects}
            projects={projects}
            selectedMasterProjectId={selectedMasterProjectId}
            selectedProjectId={selectedProjectId}
            onMacroproyectosLoad={() => {}}
            onProyectosLoad={() => {}}
            onMacroSelect={setSelectedMasterProjectId}
            onProyectoSelect={setSelectedProjectId}
            onConfirm={goToNext}
            isLoadingMacros={listMasterProjectsQuery.isLoading}
            isLoadingProyectos={listProjectsQuery.isLoading}
          />
        )}
        {activeStep === 3 && (
          <RecaudoChannelSelector
            selectedChannel={selectedChannel}
            onChannelChange={(ch) => {
              setSelectedChannel(ch);
              // Reset channel-specific state when switching
              setSelectedWhatsappConnectionId("");
              setSelectedSaleIds([]);
            }}
            selectedWhatsappConnectionId={selectedWhatsappConnectionId}
            onWhatsappLineSelect={setSelectedWhatsappConnectionId}
            voiceCallConfig={voiceCallConfigQuery.data ?? null}
            isLoadingVoiceConfig={voiceCallConfigQuery.isLoading}
          />
        )}
        {activeStep === 4 &&
          (isLoadingConnections ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-4">
                <Spinner size="xl" />
                <p className="text-gray-600">Cargando conexión seleccionada...</p>
              </div>
            </div>
          ) : (
            <Step3SelectTemplate
              selectedTemplateId={selectedTemplateId}
              onSelect={setSelectedTemplateId}
              wabaId={wabaId}
              systemIdFilter="collections-outbound"
            />
          ))}
        {activeStep === 5 && (
          <RecaudoSummary
            companyName={selectedCompany?.Nombre}
            masterProjectName={selectedMasterProject ? getMasterProjectName(selectedMasterProject) : undefined}
            projectName={selectedProject ? getProjectName(selectedProject) : undefined}
            projectId={selectedProjectId ?? undefined}
            excludedCategories={excludedCategories}
            onToggleCategory={handleToggleCategory}
            onLoadOverdueSales={handleLoadOverdueSales}
            isLoading={previewOverdueSalesQuery.isFetching || updateExcludedCategoriesMutation.isLoading}
          />
        )}
        {activeStep === 6 && previewResult && (
          <RecaudoResults
            overdueSales={previewResult.overdueSales}
            totalContacts={previewResult.totalContacts}
            selectedSaleIds={selectedSaleIds}
            onSelectionChange={setSelectedSaleIds}
            onContinue={goToNext}
            maxSelectable={
              selectedChannel === "voice"
                ? Math.max(
                    0,
                    (voiceCallConfigQuery.data?.concurrency?.reserved ?? 10) -
                      (activeVoiceCountQuery.data?.activeCount ?? 0)
                  )
                : undefined
            }
          />
        )}
        {activeStep === 7 && previewResult && selectedChannel === "voice" && voiceCallConfigQuery.data && (
          <RecaudoVoiceConfirmation
            overdueSales={previewResult.overdueSales.filter((v) => selectedSaleIds.includes(v.saleId))}
            callConfigId={voiceCallConfigQuery.data.id}
            activeCount={activeVoiceCountQuery.data?.activeCount ?? 0}
            maxConcurrent={voiceCallConfigQuery.data.concurrency?.reserved ?? 10}
            onSuccess={handleClose}
          />
        )}
        {activeStep === 7 && previewResult && selectedChannel === "whatsapp" && (
          <RecaudoConfirmation
            overdueSales={previewResult.overdueSales.filter((v) => selectedSaleIds.includes(v.saleId))}
            selectedTemplateId={selectedTemplateId}
            broadcastName={broadcastName}
            onBroadcastNameChange={setBroadcastName}
            whatsappConnectionId={selectedWhatsappConnectionId}
            companyName={selectedCompany?.Nombre}
            projectName={selectedProject ? getProjectName(selectedProject) : undefined}
            onSuccess={handleClose}
          />
        )}
      </div>
      {activeStep > 0 && activeStep !== 6 && activeStep !== 7 && (
        <div className="flex justify-between border-t border-gray-200 pt-4">
          <Button variant="outline" onClick={handleBack}>
            Atrás
          </Button>
          {(activeStep === 3 || activeStep === 4) && (
            <Button
              onClick={activeStep === 3 ? handleNextFromChannel : goToNext}
              disabled={isNextDisabled()}
            >
              Siguiente
            </Button>
          )}
        </div>
      )}
      {(activeStep === 6 || activeStep === 7) && (
        <div className="flex justify-start border-t border-gray-200 pt-4">
          <Button variant="outline" onClick={handleBack}>
            Atrás
          </Button>
        </div>
      )}
    </div>
  );
};

export default RecaudoWizard;
